import React, { ChangeEvent } from "react";
import styled from "styled-components";
import Title from "../../components/title";
import { useTranslation } from "react-i18next";
import ProfileHeader from "../../components/user/header";
import MainInfo from "../../components/user/main-info";
import SecondInfo from "../../components/user/second-info";
import Headquarters from "../../components/user/headquarters";
import { Color } from "../../constants/color";
import Button from "../../components/button/button";
import Suggerimento from "../../components/tooltip/suggerimento";
import { Icons } from "../../constants/icons";
import Label from "../../components/label";
import { useEffect, useState } from "react";
import { Headquarter, User, UserPageResponse } from "../../types";
import {
  getCompanyInfoByUser,
  setCompanyInfoByUser,
} from "../../features/user/utils";
import {
  partitaIvaOCodiceFiscaleValido,
  telefonoReferenteValido,
} from "../../features/user/validazioniProfilo";
import { corpoRegistrazione } from "../../features/user/registrazione";
import Users from "../../components/user/users";
import { axiosInstance } from "../../provider/authProvider";
import { BE_BASE_URL } from "../../utils/config";
import { toast, ToastContainer } from "react-toastify";
import {
  avvisoUnico,
  messaggioDiErrore,
} from "../../features/notifiche/avvisi";
import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../../features/user/userSlice";
import { useSolaLettura } from "../../features/user/solaLettura";
import { puoGestireAzienda } from "../../constants/role";
import { emailValida } from "../../utils/email";

const UserContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2rem;
`;

const ExpandContainer = styled.div<{
  $show: boolean;
}>`
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${Color.BLUE};
  width: 100%;
  height: 4px;
  margin-top: 20px;
`;

const ExpandButton = styled.div`
  button {
    background-color: ${Color.BLUE};
    width: 100px;
    height: 20px;
    border-radius: 20px;
  }
  padding-bottom: 18px;
`;

const EditButtonGroup = styled.div<{
  $show: boolean;
}>`
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
`;

const defaultCompanyData: UserPageResponse = {
  adminFirstName: "",
  adminLastName: "",
  adminPhone: "",
  adminEmail: "",
  businessName: "",
  vatNumber: "",
  cem: "",
  tdEmployees: 0,
  tiEmployees: 0,
  totEmployees: 0,
  companyInfo: {
    sdiCode: "",
    eaiEnrollment: "",
    primaryCeaCode: "",
    level1Sector: "",
    level2SectorTable: "",
    inailNumber: "",
    salesRange: 0,
    legalRepresentative: "",
    registeredOfficeAddress: "",
    registeredOfficeTelephone: "",
  },
  headquarters: [],
  subscriptionType: "",
  subscriptionCourses: new Set<string>(),
  admins: new Set<User>(),
};

/*
 Le validazioni del salvataggio dell'anagrafica parlano tutte dallo stesso avviso: chi
 corregge un campo e ne sbaglia un altro vede il messaggio nuovo al posto del vecchio.
*/
const AVVISO_SALVATAGGIO = "anagrafica-azienda-salvataggio";

/*
 Toglie da un oggetto le chiavi che arrivano `null` o assenti, cosi' nel merge con
 `defaultCompanyData` vince il valore vuoto di default. A chi non e' ADMIN o SUPERADMIN
 `GET /company/getByUser` manda `null` i dati personali del referente: senza questo passaggio
 lo spread li riscriverebbe sopra le stringhe vuote e a video comparirebbe «null null».
*/
function senzaValoriNulli<T extends object>(
  oggetto: T | null | undefined
): { [K in keyof T]?: NonNullable<T[K]> } {
  return Object.fromEntries(
    Object.entries(oggetto ?? {}).filter(([, valore]) => valore != null)
  ) as { [K in keyof T]?: NonNullable<T[K]> };
}

const UserPage = () => {
  const { t } = useTranslation();
  const [showSecondInfo, setShowSecondInfo] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  // Inizializziamo lo stato con il defaultCompanyData
  const [companyData, setCompanyData] =
    useState<UserPageResponse>(defaultCompanyData);
  const [newCompanyData, setNewCompanyData] =
    useState<UserPageResponse>(defaultCompanyData);
  const [newHeadquarters, setNewHeadquarters] = useState<Headquarter[]>(
    defaultCompanyData.headquarters
  );
  /*
   Solo ADMIN e SUPERADMIN possono salvare profilo, sedi e utenze (vedi `puoGestireAzienda`):
   agli altri «Modifica» e «Salva» non compaiono, e senza «Modifica» restano nascosti anche
   aggiunta e rimozione di sedi e utenti, che dipendono da `edit`. La pagina resta leggibile.
  */
  const utente = useAppSelector(selectUser);
  // In sola lettura (abbonamento disattivato, ODOO 300) nessuno salva, neanche l'ADMIN.
  const solaLettura = useSolaLettura();
  const puoModificare = puoGestireAzienda(utente?.role) && !solaLettura;

  const validateAdmins = (admins: Set<User>) => {
    // Convertiamo lo Set in array per iterare
    const adminsArray = Array.from(admins);
    for (const user of adminsArray) {
      if (
        !user.firstName.trim() ||
        !user.lastName.trim() ||
        !user.email.trim() ||
        !user.username.trim() ||
        user.role === 0 // oppure un controllo diverso per un ruolo non valido
      ) {
        return false;
      }
    }
    return true;
  };

  const handleEdit = (
    field: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.FormEvent<HTMLButtonElement>,
    index?: number,
    headquarters?: Headquarter[],
    salesValue?: string,
    admins?: Set<User>
  ) => {
    if (field.startsWith("admins.") && typeof index === "number") {
      const [, property] = field.split(".");
      // Usa il set passato come parametro se disponibile, altrimenti quello presente nello stato
      const currentAdmins = admins || newCompanyData.admins || new Set<User>();
      const adminsArray = Array.from(currentAdmins);
      if (adminsArray[index]) {
        if (property === "role") {
          adminsArray[index] = {
            ...adminsArray[index],
            [property]: Number(
              (event as React.ChangeEvent<HTMLInputElement>).target.value
            ),
          };
        } else {
          adminsArray[index] = {
            ...adminsArray[index],
            [property]: (event as React.ChangeEvent<HTMLInputElement>).target
              .value,
          };
        }
        // Aggiorna lo stato usando il set aggiornato
        setNewCompanyData((prev) => ({
          ...prev,
          admins: new Set(adminsArray),
        }));
      }
      return;
    }
    if (event) {
      if (field.includes(".")) {
        const fields = field.split(".");
        if (fields[0] === "companyInfo") {
          const value = (event.currentTarget as HTMLInputElement)
            ? event.currentTarget.value
            : (event.target! as HTMLInputElement).value;

          // Remove any trailing spaces from the field name to prevent duplicate keys
          const fieldName = fields[1].trim();

          setNewCompanyData((prev) => ({
            ...prev,
            companyInfo: { ...prev.companyInfo, [fieldName]: value },
          }));
        } else if (fields[0] === "headquarter" && index !== -1) {
          const updatedHeadquarters = [...headquarters!];
          if (fields[1] === "headquarterName") {
            updatedHeadquarters[index!].headquarterName = (
              event.target! as HTMLInputElement
            ).value;
          }
          if (fields[1] === "operativeOfficeAddress") {
            updatedHeadquarters[index!].operativeOfficeAddress = (
              event.target! as HTMLInputElement
            ).value;
          }
          if (fields[1] === "operativeOfficeTelephone") {
            updatedHeadquarters[index!].operativeOfficeTelephone = (
              event.target! as HTMLInputElement
            ).value;
          }
          setNewHeadquarters(updatedHeadquarters);
        }
      } else
        setNewCompanyData((prev) => ({
          ...prev,
          [field]: (event.target! as HTMLInputElement).value,
        }));
    } else
      setNewCompanyData((prev) => ({
        ...prev,
        companyInfo: { ...prev.companyInfo, salesRange: Number(salesValue!) },
      }));
  };

  // Nel metodo handleSave
  const handleSave = async () => {
    if (!newCompanyData) {
      avvisoUnico(
        AVVISO_SALVATAGGIO,
        "error",
        "I dati aziendali non sono ancora stati caricati"
      );
      return;
    }
    const newUserPageResponse: UserPageResponse = {
      ...newCompanyData,
      headquarters: newHeadquarters!,
    };

    newUserPageResponse.headquarters = newHeadquarters!;
    const reaRegexp = new RegExp(/^[A-Za-z]{2}[0-9]{6}$/);
    const atecoRegexp = new RegExp(/^[0-9]{2}\.[0-9]{2}\.[0-9]{2}$/);
    if (!validateAdmins(newUserPageResponse.admins)) {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", "Compilare tutti i campi obbligatori per gli utenti");
      return;
    }
    if (!emailValida(newUserPageResponse.adminEmail)) {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", "Verificare l'email");
      return;
    }
    if (!emailValida(newUserPageResponse.cem)) {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", "Verificare la pec");
      return;
    }
    if (!telefonoReferenteValido(newUserPageResponse.adminPhone)) {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", "Verificare il numero di telefono");
      return;
    }
    if (!partitaIvaOCodiceFiscaleValido(newUserPageResponse.vatNumber)) {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", "Verificare la partita iva o codice fiscale");
      return;
    }

    // Validations for character limits
    if (
      newUserPageResponse.companyInfo.eaiEnrollment &&
      newUserPageResponse.companyInfo.eaiEnrollment.length > 6
    ) {
      avvisoUnico(AVVISO_SALVATAGGIO, "warning", "Il codice REA non può superare i 6 caratteri.");
      return;
    }
    if (
      newUserPageResponse.companyInfo.primaryCeaCode &&
      newUserPageResponse.companyInfo.primaryCeaCode.length > 7
    ) {
      avvisoUnico(
        AVVISO_SALVATAGGIO,
        "warning",
        "Il codice Ateco primario non può superare i 7 caratteri."
      );
      return;
    }
    if (
      newUserPageResponse.companyInfo.inailNumber &&
      newUserPageResponse.companyInfo.inailNumber.length > 8
    ) {
      avvisoUnico(
        AVVISO_SALVATAGGIO,
        "warning",
        "Il numero INAIL non può superare gli 8 caratteri."
      );
      return;
    }
    if (
      newUserPageResponse.companyInfo.sdiCode &&
      newUserPageResponse.companyInfo.sdiCode.length > 7
    ) {
      avvisoUnico(AVVISO_SALVATAGGIO, "warning", "Il codice SDI non può superare i 7 caratteri.");
      return;
    }
    if (
      newUserPageResponse.companyInfo.registeredOfficeTelephone &&
      newUserPageResponse.companyInfo.registeredOfficeTelephone.length > 10
    ) {
      avvisoUnico(
        AVVISO_SALVATAGGIO,
        "warning",
        "Il telefono della sede legale non può superare i 10 caratteri."
      );
      return;
    }

    /*
    if (
      newUserPageResponse.companyInfo.sdiCode.length !== 7 &&
      newUserPageResponse.companyInfo.sdiCode.length !== 6
    ) {
      alert("Verificare il codice SDI");
      return;
    }
    
    if (!reaRegexp.test(newUserPageResponse.companyInfo.eaiEnrollment)) {
      alert("Verificare il codice REA");
      return;
    }
    if (!atecoRegexp.test(newUserPageResponse.companyInfo.primaryCeaCode)) {
      alert("Verificare il codice Ateco primario");
      return;
    }
      */

    // Clean up companyInfo object to remove any keys with trailing spaces
    if (newUserPageResponse.companyInfo) {
      const cleanCompanyInfo: any = {};

      // Copy all properties with trimmed keys
      Object.entries(newUserPageResponse.companyInfo).forEach(
        ([key, value]) => {
          const trimmedKey = key.trim();
          // Only keep the trimmed key version
          cleanCompanyInfo[trimmedKey] = value;
        }
      );

      newUserPageResponse.companyInfo = cleanCompanyInfo;
    }

    // Continue with your existing code...
    try {
      const companyResponse = await setCompanyInfoByUser(newUserPageResponse);
      if (companyResponse?.status == 200) {
        avvisoUnico(
          AVVISO_SALVATAGGIO,
          "success",
          "Informazioni aziendali aggiornate con successo"
        );
      } else {
        avvisoUnico(
          AVVISO_SALVATAGGIO,
          "error",
          "Verificare che le informazioni inserite siano corrette"
        );
        //console.log("error", companyResponse);
        return;
      }
    } catch (error: any) {
      /*
       Anche il rifiuto che arriva dal server passa dal meccanismo nuovo: la validazione di
       partita iva e telefono sta nascendo anche dietro, e un rifiuto del backend non deve
       comparire in una forma diversa da quello del browser.
      */
      avvisoUnico(
        AVVISO_SALVATAGGIO,
        "error",
        messaggioDiErrore(
          error,
          "Errore nel salvataggio delle informazioni aziendali"
        )
      );
      return;
    }

    const existingAdmins = Array.from(newUserPageResponse.admins || []).filter(
      (user) => user.id !== 0 && !user.isDeleted
    );
    const newAdmins = Array.from(newUserPageResponse.admins || []).filter(
      (user) => user.id === 0 && !user.isDeleted
    );

    const registerAdmins = async () => {
      const results = await Promise.allSettled(
        newAdmins.map((user) =>
          // Senza password: l'utente se la imposta dal link dell'email di benvenuto.
          axiosInstance.post(
            `${BE_BASE_URL}/user/registration`,
            corpoRegistrazione(user)
          )
        )
      );
      const registrationErrors = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (registrationErrors.length > 0) {
        const errorMessages = registrationErrors
          .map(
            (result) =>
              result.reason.response?.data?.message ||
              "Errore nella registrazione"
          )
          .join(", ");
        throw new Error(
          `Alcuni utenti non sono stati registrati: ${errorMessages}`
        );
      }
      return results;
    };

    // Funzione wrapper per l'aggiornamento degli admin esistenti
    const updateAdmins = async () => {
      const results = await Promise.allSettled(
        existingAdmins.map((user) => {
          const payload = {
            id: user.id,
            firstName: user.firstName.trim(),
            lastName: user.lastName.trim(),
            username: user.username.trim(),
            email: user.email.trim(),
            isUsing2FA: false,
            role: user.role,
          };
          return axiosInstance.put(`${BE_BASE_URL}/user/update`, payload);
        })
      );
      const updateErrors = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (updateErrors.length > 0) {
        const errorMessages = updateErrors
          .map(
            (result) =>
              result.reason.response?.data?.message || "Errore nell'update"
          )
          .join(", ");
        throw new Error(
          `Alcuni utenti non sono stati aggiornati: ${errorMessages}`
        );
      }
      return results;
    };

    try {
      // Gestione della registrazione dei nuovi admin con toast.promise
      await toast.promise(registerAdmins(), {
        pending: {
          render() {
            return "Registrazione utenti in corso...";
          },
          icon: false,
        },
        success: {
          render({ data }) {
            return "Registrazione utenti completata";
          },
        },
        error: {
          render({ data }) {
            const errorData = data as { message: string };
            return errorData.message;
          },
        },
      });

      await toast.promise(updateAdmins(), {
        pending: "Aggiornamento utenti in corso...",
        success: "Aggiornamento utenti completato",
        error: {
          render({ data }) {
            const errorData = data as { message: string };
            return errorData.message;
          },
        },
      });
    } catch (error) {
      // Se una delle due operazioni fallisce, l'errore è già mostrato dal toast.promise
      return;
    }

    // Dopo aver completato le operazioni, attendiamo un po' prima di ricaricare
    setTimeout(() => {
      //console.log("Stato finale prima del reload:", newHeadquarters);
      window.location.reload();
    }, 4000);
  };

  useEffect(() => {
    getCompanyInfoByUser()!.then((response) => {
      // Assicuriamoci che headquarters sia sempre un array
      let headquartersArray = response.headquarters;
      if (!Array.isArray(headquartersArray)) {
        /*
        //console.log(
          "Headquarters non è un array, tipo:",
          typeof headquartersArray
        );
*/
        // Se è un oggetto ma non un array, prova a convertirlo
        if (
          typeof headquartersArray === "object" &&
          headquartersArray !== null
        ) {
          try {
            // Se ha proprietà length e keys numerici, potrebbe essere array-like
            if ("length" in headquartersArray) {
              headquartersArray = Array.from(headquartersArray as any);
            } else {
              // Altrimenti prova a estrarre i valori dell'oggetto
              headquartersArray = Object.values(headquartersArray);
            }
            //console.log("Headquarters convertiti:", headquartersArray);
          } catch (e) {
            //console.error("Errore nella conversione degli headquarters:", e);
            headquartersArray = [];
          }
        } else {
          // Fallback se proprio non si riesce a gestirlo
          headquartersArray = [];
        }
      }

      // Esegui un merge tra defaultCompanyData e la risposta
      const mergedCompany: UserPageResponse = {
        ...defaultCompanyData,
        ...senzaValoriNulli(response),
        companyInfo: {
          ...defaultCompanyData.companyInfo,
          ...senzaValoriNulli(response.companyInfo),
        },
        headquarters: headquartersArray,
        subscriptionCourses:
          response.subscriptionCourses ||
          defaultCompanyData.subscriptionCourses,
        // Ai ruoli che non gestiscono l'azienda l'elenco arriva vuoto: la sezione resta senza righe.
        admins: new Set(response.admins ?? []),
      };

      setCompanyData(mergedCompany);
      setNewCompanyData(mergedCompany);
      setNewHeadquarters(headquartersArray);
    });
  }, []);

  return (
    <UserContainer>
      <ToastContainer />
      <Title text={t("user-title")} />
      <ProfileHeader companyData={newCompanyData} />
      <MainInfo
        edit={edit}
        solaLettura={solaLettura}
        companyData={newCompanyData}
        editCompanyData={(field, event) => {
          handleEdit(field, event);
        }}
      />
      <ExpandContainer $show={!showSecondInfo}>
        <Label
          text={t(puoModificare ? "Espandi e modifica" : "user-espandi")}
          color={Color.BLUE}
          fontSize={"13px"}
        />
        <ExpandButton>
          <Button
            color={Color.BLUE}
            aria-label={t(puoModificare ? "user-espandi-e-modifica" : "user-espandi-profilo")}
            icon={Icons.COMMONS.ARROW}
            onClick={() => setShowSecondInfo(true)}
          />
        </ExpandButton>
      </ExpandContainer>
      <SecondInfo
        show={showSecondInfo}
        edit={edit}
        companyData={newCompanyData}
        editCompanyData={(field, event, salesValue) =>
          handleEdit(field, event, undefined, undefined, salesValue)
        }
      />
      <Headquarters
        show={showSecondInfo}
        edit={edit}
        companyData={newCompanyData}
        editCompanyData={(field, event, index, headquarters) =>
          handleEdit(field, event, index, headquarters)
        }
        updateHeadquarters={(newHeadquartersData) =>
          setNewHeadquarters(newHeadquartersData)
        }
      />
      <Users
        show={showSecondInfo}
        edit={edit}
        companyData={newCompanyData}
        editCompanyData={(field, event, index, admins) =>
          handleEdit(field, event, index, undefined, undefined, admins)
        }
        updateNewCompanyData={(newAdmins) =>
          setNewCompanyData((prev) => ({ ...prev, admins: newAdmins }))
        }
      />
      <EditButtonGroup $show={showSecondInfo && puoModificare}>
        <Suggerimento testo={t("user-aiuto-salva")}>
          <Button
            color={Color.BLUE}
            textColor={Color.WHITE}
            label={t("reserved-courses-detail-button-save")}
            onClick={() => handleSave()}
          />
        </Suggerimento>
        <Suggerimento testo={t("user-aiuto-modifica")}>
          <Button
            color={Color.WHITE}
            textColor={Color.LIGHT_BLUE}
            label={t("reserved-courses-detail-button-edit")}
            onClick={() => setEdit(!edit)}
          />
        </Suggerimento>
      </EditButtonGroup>
    </UserContainer>
  );
};

export default UserPage;
